import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import Shepherd from "shepherd.js";

import ButtonIcon from "../../images/rebrandIcons/ButtonIcon.png";

function createTourButtonHTML(label, direction = null, t) {
  const text = t(label);

  const arrowLeft = `<img class="h-2.5 mr-3 mb-1 transform rotate-180" src="${ButtonIcon}" alt="${text} Arrow Icon" />`;
  const arrowRight = `<img class="h-2.5 ml-3 mb-1" src="${ButtonIcon}" alt="${text} Arrow Icon" />`;

  return `
    <div class="flex gap-1 items-center">
      ${direction === "left" ? arrowLeft : ""}
      <p>${text}</p>
      ${direction === "right" ? arrowRight : ""}
    </div>
  `;
};

function useSongsAddAlbumTour() {
  const { t } = useTranslation(["tutorial", "onboarding", "common"]);
  const startTour = useCallback(() => {
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: "bg-white font-sentric rounded-[10px] text-gray-800 shadow-xl p-6",
        cancelIcon: { enabled: true },
        scrollTo: { behavior: "smooth", block: "center" },
      },
    });

    // The Album Spotify Step
    tour.addStep({
      id: "albumSpotifyStepTour",
      text: t("tutorial:add-album-import"),
      attachTo: {
        element: "#albumSpotifyStep",
        on: "bottom",
      },
      beforeShowPromise: () => waitForSelector("#albumSpotifyStep"),
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      classes: "ml-2",
      buttons: [
        { text: createTourButtonHTML("writers:close", "close", t), action: tour.next },
      ],
    });

    // The Album Select Songs Step
    tour.addStep({
      id: "albumSelectSongsStepTour",
      text: t("tutorial:add-album-select-songs"),
      attachTo: {
        element: "#albumSelectSongsStep",
        on: "bottom",
      },
      beforeShowPromise: () => waitForSelector("#albumSelectSongsStep"),
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      classes: "ml-2",
      buttons: [
        { text: createTourButtonHTML("writers:close", "close", t), action: tour.next },
      ],
    });

    // The Album Choose Writers Step
    tour.addStep({
      id: "albumChooseWritersStepTour",
      text: t("tutorial:add-album-choose-writers"),
      attachTo: {
        element: "#albumChooseWritersStep",
        on: "bottom",
      },
      beforeShowPromise: () => waitForSelector("#albumChooseWritersStep"),
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      classes: "ml-2",
      buttons: [
        { text: createTourButtonHTML("writers:close", "close", t), action: tour.next },
      ],
    });

    // The Album Assign Writers Individual Step
    tour.addStep({
      id: "albumAssignWritersIndividualStepTour",
      text: t("tutorial:add-album-assign-writers-individual"),
      attachTo: {
        element: "#albumAssignWritersIndividualStep",
        on: "bottom",
      },
      beforeShowPromise: () => waitForSelector("#albumAssignWritersIndividualStep"),
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      classes: "ml-2",
      buttons: [
        { text: createTourButtonHTML("common:next-button", "right", t), action: tour.next },
      ],
    });

    // The Album Assign Writers Bulk Step
    tour.addStep({
      id: "albumAssignWritersBulkStepTour",
      text: t("tutorial:add-album-assign-writers-bulk"),
      attachTo: {
        element: "#albumAssignWritersBulkStep",
        on: "bottom",
      },
      beforeShowPromise: () => waitForSelector("#albumAssignWritersBulkStep"),
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      classes: "ml-2",
      buttons: [
        { text: createTourButtonHTML("writers:close", "close", t), action: tour.next },
      ],
    });

    // The Album Song Progress Step
    tour.addStep({
      id: "albumSongProgressStepTour",
      text: t("tutorial:add-album-song-progress"),
      attachTo: {
        element: "#albumSongProgressStep",
        on: "bottom",
      },
      beforeShowPromise: () => waitForSelector("#albumSongProgressStep"),
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      classes: "ml-2",
      buttons: [
        { text: createTourButtonHTML("common:next-button", "right", t), action: tour.next },
      ],
    });

    // The Album Song Details Step
    tour.addStep({
      id: "albumSongDetailsStepTour",
      text: t("tutorial:add-album-song-details"),
      attachTo: {
        element: "#albumSongDetailsStep",
        on: "bottom",
      },
      beforeShowPromise: () => waitForSelector("#albumSongDetailsStep"),
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      classes: "ml-2",
      buttons: [
        { text: createTourButtonHTML("common:next-button", "right", t), action: tour.next },
      ],
    });

    // The Album Writer Shares Step
    tour.addStep({
      id: "albumWriterSharesStepTour",
      text: t("tutorial:add-album-writer-shares"),
      attachTo: {
        element: "#albumWriterSharesStep",
        on: "bottom",
      },
      beforeShowPromise: () => waitForSelector("#albumWriterSharesStep"),
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      classes: "ml-2",
      buttons: [
        { text: createTourButtonHTML("common:next-button", "right", t), action: tour.next },
      ],
    });

    // The Album Song Navigation Step
    tour.addStep({
      id: "albumSongNavStepTour",
      text: t("tutorial:add-album-song-navigation"),
      attachTo: {
        element: "#albumSongNavStep",
        on: "bottom",
      },
      beforeShowPromise: () => waitForSelector("#albumSongNavStep"),
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      classes: "ml-2",
      buttons: [
        { text: createTourButtonHTML("writers:close", "close", t), action: tour.next },
      ],
    });

    // The Album Review Step
    tour.addStep({
      id: "albumReviewStepTour",
      text: t("tutorial:add-album-review-songs"),
      attachTo: {
        element: "#albumReviewStep",
        on: "top",
      },
      beforeShowPromise: () => waitForSelector("#albumReviewStep"),
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      classes: "ml-2",
      buttons: [
        { text: createTourButtonHTML("common:next-button", "right", t), action: tour.next },
      ],
    });

    // The Song End Buttons Section Step
    tour.addStep({
      id: "albumSubmitStepTour",
      text: t("tutorial:add-album-submit"),
      attachTo: {
        element: "#albumSubmitStep",
        on: "left",
      },
      beforeShowPromise: () => waitForSelector("#albumSubmitStep"),
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      buttons: [
        {
          text: createTourButtonHTML("onboarding:back-button", "left", t),
          action: tour.back,
        },
        {
          text: createTourButtonHTML("onboarding:complete-button", null, t),
          action: () => {
            localStorage.setItem("songsAddAlbumTourCompleted", "true");
            tour.complete();
          },
        },
      ],
    });

    tour.start();
  }, [t]);

  return startTour;
}

export default useSongsAddAlbumTour;

// Utility: wait until a DOM element exists or timeout
function waitForSelector(selector, { timeout = 8000 } = {}) {
  return new Promise((resolve, reject) => {
    const found = document.querySelector(selector);
    if (found)
      return resolve(found);

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      reject(new Error(`Timed out waiting for ${selector}`));
    }, timeout);
  });
}
